import React, { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { gql } from '~/__generated__'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Button } from '~/ui/button'
import { Badge } from '~/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/ui/table'
import { useCurrentOrganization } from '~/auth/use-current-organization'
import { useToast } from '~/hooks/use-toast'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { InvoicesTable } from './components/invoices-table'
import { HardwareProducts } from './components/hardware-products'
import { CreditCard } from 'lucide-react'

const LOCATIONS_FOR_SELECT_QUERY = gql(`
  query BillingLocationsForSelect {
    locationsForSelect {
      id
      name
      zones {
        id
        name
      }
    }
  }
`)


const ORGANIZATION_SUBSCRIPTIONS_QUERY = gql(`
  query OrganizationSubscriptions($organizationId: ID!) {
    organizationSubscriptions(organizationId: $organizationId) {
      edges {
        node {
          id
          status
          currentPeriodStart
          currentPeriodEnd
          cancelAtPeriodEnd
          billingPlan {
            id
            name
            slug
            monthlyBasePriceCents
            entitlements
            restrictions
          }
          zone {
            id
            name
          }
        }
      }
    }
  }
`)

const ORGANIZATION_INVOICES_QUERY = gql(`
  query OrganizationInvoices($organizationId: ID!, $limit: Int) {
    organizationInvoices(organizationId: $organizationId, limit: $limit) {
      id
      amount
      status
      createdAt
      pdfUrl
      hostedInvoiceUrl
      number
    }
  }
`)

const CREATE_CUSTOMER_PORTAL_SESSION_MUTATION = gql(`
  mutation CreateCustomerPortalSession($input: CreateCustomerPortalSessionInput!) {
    createCustomerPortalSession(input: $input) {
      customerPortalSession {
        url
      }
      errors
    }
  }
`)

export const BillingScreen = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { id: organizationId } = useCurrentOrganization()
  const [activeTab, setActiveTab] = useState('zones')
  const { data: locationsData } = useQuery(LOCATIONS_FOR_SELECT_QUERY)
  const { data: subscriptionsData } = useQuery(
    ORGANIZATION_SUBSCRIPTIONS_QUERY,
    {
      variables: { organizationId: organizationId ?? '' },
      skip: !organizationId,
    }
  )
  const { data: invoicesData, loading: invoicesLoading } = useQuery(
    ORGANIZATION_INVOICES_QUERY as any,
    {
      variables: { organizationId: organizationId ?? '', limit: 20 },
      skip: !organizationId,
    }
  )

  const [createCustomerPortalSession] = useSafeMutation(CREATE_CUSTOMER_PORTAL_SESSION_MUTATION)

  const subscriptions = subscriptionsData?.organizationSubscriptions?.edges?.map((e: any) => e.node) || []
  const invoices = invoicesData?.organizationInvoices || []
  const locations = locationsData?.locationsForSelect || []
  const zones = useMemo(
    () => locations.flatMap((l: any) => (l.zones || []).map((z: any) => ({ ...z, location: l }))),
    [locations]
  )

  const subscriptionByZoneId = useMemo(() => {
    const map = new Map<string, any>()
    subscriptions.forEach((s: any) => {
      if (s.zone?.id) map.set(s.zone.id, s)
    })
    return map
  }, [subscriptions])


  const handleManageBilling = async () => {
    const returnUrl = `${window.location.origin}/billing`
    const result = await createCustomerPortalSession({
      variables: {
        input: {
          returnUrl,
        },
      },
    })

    if (result.data?.createCustomerPortalSession?.customerPortalSession?.url) {
      window.location.href = result.data.createCustomerPortalSession.customerPortalSession.url
    } else {
      const errors = result.data?.createCustomerPortalSession?.errors || ['Failed to create portal session']
      toast({
        title: 'Error',
        description: errors.join(', '),
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="flex-1 space-y-6 overflow-y-auto p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold">Billing</h1>
            <p className="mt-1 text-gray-600">Manage your zones, billing invoices, and hardware</p>
          </div>
          <Button onClick={handleManageBilling} variant="outline">
            <CreditCard className="mr-2 h-4 w-4" />
            Manage Billing
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="zones">
              Zones
              {zones.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {zones.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="invoices">
              Invoices
              {invoices.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {invoices.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="hardware">Hardware</TabsTrigger>
          </TabsList>

          <TabsContent value="zones" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Zones & Subscriptions</CardTitle>
                <CardDescription>
                  View all your zones with their subscription status, billing plans, and renewal dates. Manage individual zone billing settings.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Location</TableHead>
                      <TableHead>Zone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Renewal/Expiration</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {zones.map((z: any) => {
                      const sub = subscriptionByZoneId.get(z.id)
                      const getStatusBadge = (status: string) => {
                        switch (status) {
                          case 'active':
                          case 'trialing':
                            return <Badge variant="default" className="bg-green-500">Active</Badge>
                          case 'past_due':
                            return <Badge variant="destructive">Past Due</Badge>
                          case 'canceled':
                            return <Badge variant="secondary">Canceled</Badge>
                          case 'incomplete':
                          case 'incomplete_expired':
                            return <Badge variant="outline">Incomplete</Badge>
                          default:
                            return <Badge variant="outline">{status}</Badge>
                        }
                      }

                      const formatDate = (dateString: string | null) => {
                        if (!dateString) return '—'
                        return new Date(dateString).toLocaleDateString()
                      }

                      const getRenewalText = (subscription: any) => {
                        if (!subscription) return '—'

                        const endDate = subscription.currentPeriodEnd
                        if (!endDate) return '—'

                        const formattedDate = formatDate(endDate)

                        if (subscription.cancelAtPeriodEnd) {
                          return `Expires ${formattedDate}`
                        } else {
                          return `Renews ${formattedDate}`
                        }
                      }

                      return (
                        <TableRow key={z.id}>
                          <TableCell>{z.location?.name}</TableCell>
                          <TableCell>{z.name}</TableCell>
                          <TableCell>
                            {sub ? getStatusBadge(sub.status) : <span className="text-gray-500">No subscription</span>}
                          </TableCell>
                          <TableCell>
                            {sub?.billingPlan ? (
                              <div>
                                <div className="font-medium">{sub.billingPlan.name}</div>
                                {sub.billingPlan.monthlyBasePriceCents ? (
                                  <div className="text-xs text-gray-500">
                                    ${(sub.billingPlan.monthlyBasePriceCents / 100).toFixed(0)}/mo
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={sub?.cancelAtPeriodEnd ? 'text-red-600' : 'text-green-600'}>
                              {getRenewalText(sub)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/zones/${z.id}/billing`)}
                            >
                              Manage
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invoices">
            <InvoicesTable invoices={invoices} loading={invoicesLoading} />
          </TabsContent>

          <TabsContent value="hardware">
            <HardwareProducts />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
